/**
 * The mouse-drag gesture, once.
 *
 * Every drag on the canvas is the same five things — swallow the press, hold a
 * cursor, follow the pointer, put the cursor back, commit — and they were
 * written out at each site. Four of them lived in `useTableResize` alone, which
 * meant eleven `removeEventListener` pairs and five save/restore blocks; when
 * "a press that never moved must commit nothing" turned out to be needed, it
 * was added to three of the four and missed on the fourth.
 *
 * Each caller now supplies only its own arithmetic.
 */

/** The part of a press this module needs — a `React.MouseEvent` satisfies it,
 *  and so does anything a test hands in. */
export interface PressEvent {
  clientX: number;
  clientY: number;
  preventDefault: () => void;
  stopPropagation: () => void;
}

export interface DragSpec {
  /** Held on `<body>` for the length of the gesture, so the cursor does not
   *  flicker back to the default over whatever the pointer crosses. */
  cursor: string;
  /** Each move, with the offset from the press in CSS pixels. */
  onMove: (dx: number, dy: number) => void;
  /**
   * The release — but only if the pointer actually moved.
   *
   * **A press that never moved commits nothing.** The sizes a drag starts from
   * are MEASURED, and a measurement is not always what is stored: a lone column
   * fills its table whatever width the column itself carries, so clicking its
   * edge wrote the rendered width over the authored one and the cell jumped to
   * the full width of the table. Invisible with several columns, where the two
   * agree; glaring with one, where they do not. Nothing moved, so nothing
   * changed — and the undo stack does not gain an entry for a click either.
   */
  onCommit: () => void;
  /** The release either way, before `onCommit`: drop whatever draft the moves
   *  were painting. */
  onSettle?: () => void;
}

/**
 * Start a drag from a press. Returns nothing — the gesture owns itself from
 * here and unhooks on release.
 */
export function beginDrag(e: PressEvent, spec: DragSpec): void {
  e.preventDefault();
  e.stopPropagation();

  const startX = e.clientX;
  const startY = e.clientY;
  let moved = false;

  document.body.style.cursor = spec.cursor;
  document.body.style.userSelect = 'none';

  const onMove = (me: MouseEvent) => {
    moved = true;
    spec.onMove(me.clientX - startX, me.clientY - startY);
  };

  const onUp = () => {
    window.removeEventListener('mousemove', onMove);
    window.removeEventListener('mouseup', onUp);
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
    spec.onSettle?.();
    if (moved) spec.onCommit();
  };

  window.addEventListener('mousemove', onMove);
  window.addEventListener('mouseup', onUp);
}

/**
 * Swallow the click that fires when a drag ends somewhere other than it began.
 *
 * A `click` is dispatched on the nearest COMMON ANCESTOR of the press and the
 * release, so dragging an element ends with a click on the page sheet — whose
 * own handler clears the selection. The element was selected by the press,
 * moved, and then deselected by an event nobody aimed at anything: the toolbar
 * appeared for exactly as long as the drag lasted and vanished on release.
 * Stopping the click AT the handle cannot help, because by then the click is
 * not on the handle.
 *
 * Capture phase, so it runs before the sheet's handler, and one shot. The
 * `mouseup` fallback removes it when no click follows at all — a listener that
 * outlives its gesture would eat the author's next one.
 */
export function suppressClickAfterDrag(): void {
  const suppress = (ev: Event) => {
    ev.stopImmediatePropagation();
    ev.preventDefault();
    cleanup();
  };
  const cleanup = () => {
    window.removeEventListener('click', suppress, true);
    window.removeEventListener('mouseup', onRelease, true);
  };
  const onRelease = () => {
    // A click follows a release immediately or not at all; give it that tick.
    setTimeout(cleanup, 0);
  };
  window.addEventListener('click', suppress, true);
  window.addEventListener('mouseup', onRelease, true);
}
